import { getNow, getClosestSchoolYearSwitch, isBeforeOrEqual, printDate, isAfter, isBefore } from '@kathondvla/sri-client/date-utils';
import { addErrorNotification, addWarningNotification } from 'ReduxLoop/notifications/notificationActions';
import { getKeyAndMeta } from '../save';
import { getCurrentRelationWithOu, getPreviousRelationWithOu, getNextRelationWithOu } from '../dataAccess';
import { getPeriodInfo } from '../transformations';

const KATH_OND_VLA_HREFS = {
  vos: '/organisations/c000eaea-c451-2590-e044-d4856467bfb8',
  sam: '/sam/organisationalunits/c000eaea-c451-2590-e044-d4856467bfb8',
};

const addQuotesWhenStringHasSpaces = (str) => {
  return str.includes(' ') ? `'${str}'` : str;
};

const getNewLabel = (config) => {
  if (config.newLabel) {
    return config.newLabel;
  }
  if (config.canOnlyHaveOne) {
    return `nieuw${config.isNeuter ? '' : 'e'} ${config.label.toLowerCase()}`;
  }
  return `voeg ${addQuotesWhenStringHasSpaces(config.label)} toe`;
}

class RelationsComponents {
  constructor($scope, $ngRedux) {
    'ngInject';

    this.$ngRedux = $ngRedux;
    this.$scope = $scope;
    this.printDate = printDate;

    // Listen for the reset event
    this.$scope.$on('resetOuAdminForm', () => {
      this.reset();
    });
  }

  setLabels = () => {
    this.currentOuLabel = '';
    this.previousOuLabel = '';
    this.nextOuLabel = '';
    this.currentOuLabelAfter = '';
    this.previousOuLabelAfter = '';
    this.nextOuLabelAfter = '';
    if (this.config.type === 'IS_PART_OF' && this.config.thisOu === 'from') {
      this.currentOuLabelAfter = ` heeft momenteel als onderdeel`;
      this.previousOuLabelAfter = ` had hiervoor als onderdeel`;
      this.nextOuLabelAfter = ` heeft hierna als onderdeel`;
    } else if (this.config.type === 'IS_PART_OF' && this.config.thisOu === 'to') {
      this.currentOuLabelAfter = ` is momenteel onderdeel van`;
      this.previousOuLabelAfter = ` was hiervoor onderdeel van`;
      this.nextOuLabelAfter = ` is hierna onderdeel van`;
    } else {
      const genericPart = this.config.isOnlyOne
        ? `${this.ouTypeConfig.isNeuter ? '' : 'e'} ${addQuotesWhenStringHasSpaces(this.ouTypeConfig.label.toLowerCase())} van`
        : `${this.config.isNeuter ? '' : 'e'} ${addQuotesWhenStringHasSpaces(this.config.label.toLowerCase())}`;
      this.currentOuLabel = `huidig${genericPart} `;
      this.previousOuLabel = `vorig${genericPart} `;
      this.nextOuLabel = `volgend${genericPart}`;
    }
  }

  $onInit() {
    this.newLabel = getNewLabel(this.config);
    this.searchParams = { types: 'organisational_unit', organisationalUnitTypes: Array.from(this.config.ouTypes).join(','), expand: 'full', limit: 50 };
    this.directSearchParams = { typeIn: Array.from(this.config.ouTypes).join(','), status: 'ACTIVE,FUTURE' };
    this.directSearchPath = `/${this.ouTypeConfig.domain}/organisationalunits`;
    this.setLabels();
  }

  reset() {
    this.newOu = null;
    this.editOu = null;
    this.editRelation = null;
    this.currentRelation = null;
    this.previousRelation = null;
    this.nextRelation = null;
    this.currentRelVm = null;
    this.previousRelVm = null;
    this.nextRelVm = null;
  }

  calculateVm() {
    this.$scope.$emit('onCalculateVm');
  }

  transformRelatedOu(relation) {
    const prop = relation.from.$$expanded ? 'from' : 'to';
    return {
      href: relation[prop].href,
      name: relation[prop].$$expanded.$$displayName,
      periodInfo: getPeriodInfo(relation),
    }
  }

  async fetchCurrentRelation() {
    this.currentRelation = null;
    this.currentRelVm = null;
    if (!this.newOu || !this.newOu.startDate) {
      return;
    }
    this.currentRelationPromise = await getCurrentRelationWithOu({
      domain: this.ouTypeConfig.domain,
      type: this.config.type,
      href: this.newOu.href,
      prop: this.config.thisOu === 'from' ? 'to' : 'from',
      date: getNow(),
    });
    this.currentRelation = await this.currentRelationPromise;
    if (this.currentRelation) {
      console.log('current relation found', this.currentRelation);
      this.currentRelVm = this.transformRelatedOu(this.currentRelation);
    }
    this.$scope.$apply();
  };

  async fetchLinks(ouHref, prop) {
    try {
      this.loadingLinks = true;
      let scopeApply = false;
      if(!this.pendingLinks[`${this.config.key}${ouHref}`]) {
        scopeApply = true;
        const previousRelation = await getPreviousRelationWithOu({
          domain: this.ouTypeConfig.domain,
          type: this.config.type,
          href: ouHref,
          prop,
          date: this.editRelation.startDate,
        });
        this.pendingLinks[`${this.config.key}${ouHref}`] = { previous: previousRelation ? previousRelation.$$meta.permalink : null };
        if (previousRelation) {
          this.pendingRelations[previousRelation.$$meta.permalink] = previousRelation;
        }

        const nextRelation = await getNextRelationWithOu({
          domain: this.ouTypeConfig.domain,
          type: this.config.type,
          href: ouHref,
          prop,
          date: this.editRelation.endDate,
        });
        this.pendingLinks[`${this.config.key}${ouHref}`].next = nextRelation ? nextRelation.$$meta.permalink : null;
        if (nextRelation) {
          this.pendingRelations[nextRelation.$$meta.permalink] = nextRelation;
        }
      }
      if (this.pendingLinks[`${this.config.key}${ouHref}`].previous) {
        this.previousRelation = this.pendingRelations[this.pendingLinks[`${this.config.key}${ouHref}`].previous];
        this.previousRelVm = this.transformRelatedOu(this.previousRelation);
      }
      if (this.pendingLinks[`${this.config.key}${ouHref}`].next) {
        this.nextRelation = this.pendingRelations[this.pendingLinks[`${this.config.key}${ouHref}`].next];
        this.nextRelVm = this.transformRelatedOu(this.nextRelation);
      }
      this.loadingLinks = false;
      if (scopeApply) {
        this.$scope.$apply();
      }
    } catch (error) {
      console.error('error fetching links', error);
    }
  }

  addNewRelation = () => {
    this.reset();
    this.newOu = {
      startDate: this.config.swithOnSchoolYear ? getClosestSchoolYearSwitch() : getNow(),
    };
  };

  getHref = (ou) => {
    if (this.ouTypeConfig.domain === 'sam' && ou.$$meta.permalink === KATH_OND_VLA_HREFS.vos) {
      return KATH_OND_VLA_HREFS.sam;
    }
    if (ou.$$meta.permalink.startsWith('/organisations')) {
      return `/kathondvla/organisationalunits/${ou.key}`;
    }
    return ou.$$meta.permalink;
  }

  selectNewOu = (ou) => {
    this.newOu.href = this.getHref(ou);
    this.newOu.name = ou.name || ou.$$displayName;
    this.newOu.type = ou.type === 'ANDERE' ? 'EXTERN' : ou.type;
    if (this.ouTypeConfig.domain === 'sam' && ou.$$meta.permalink === KATH_OND_VLA_HREFS.vos) {
      this.newOu.type = 'ONDERWIJSKOEPEL';
    }
    if (ou.institutionNumber) {
      this.identifiers.push({
        organisationalUnit: { href: ou.$$meta.permalink },
        value: ou.institutionNumber,
        type: 'INSTITUTION_NUMBER',
      });
    }
    if (this.config.isOnlyOne) {
      this.fetchCurrentRelation();
    }
  };

  async newRelationStartDateChanged(e, value) {
    this.newRelationStartDateErrors = [];
    this.newOu.startDate = value;
    // await this.currentRelationPromise;
    // TODO validate start date based on previous relation
    // you can not go further than the start of the previous relation because that would mean deleting it
  };

  async addRelation() {
    let scopeApply = false;
    if (
      !this.newOuForm.valid ||
      !this.newOu.href ||
      (this.vm.currentOu && this.newOu.href === this.vm.currentOu.href) ||
      (!this.config.canOnlyHaveOne && this.vm.currentOus.find((ou) => ou.href === this.newOu.href))
    ) {
      this.$ngRedux.dispatch(
        addErrorNotification(`Vul alle velden voor ${this.config.isNeuter ? 'het' : 'de'} nieuwe ${this.config.label} correct in.`)
      );
      return;
    }
    if (this.vm.currentOu) {
      this.pendingRelations[this.vm.currentOu.resource.$$meta.permalink] = {
        ...this.vm.currentOu.resource,
        endDate: this.newOu.startDate,
      };
    } else if (this.config.isOnlyOne) {
      // lookUp if there is a current relation and that one will need to be ended
      scopeApply = true;
      await this.currentRelationPromise;
      if (this.currentRelation) {
        this.pendingRelations[this.currentRelation.$$meta.permalink] = {
          ...this.currentRelation,
          endDate: this.newOu.startDate,
        };
        this.$ngRedux.dispatch(
          addWarningNotification(`De huidige ${this.config.type} relatie tussen ${this.currentRelVm.name} en ${this.newOu.name} zal bij opslaan ook beëindigd worden op ${printDate(this.newOu.startDate)}.`)	
        );
      }
    }
    const newRelation = {
      ...getKeyAndMeta(`/${this.ouTypeConfig.domain}/organisationalunits/relations`),
      type: this.config.type,
      from: this.config.thisOu === 'from'
        ? { href: this.ou.href }
        : { href: this.newOu.href, $$expanded: { $$displayName: this.newOu.name, type: this.newOu.type } },
      to: this.config.thisOu === 'to'
        ? { href: this.ou.href }
        : { href: this.newOu.href, $$expanded: { $$displayName: this.newOu.name, type: this.newOu.type } },
      startDate: this.newOu.startDate,
    };
    this.pendingRelations[newRelation.$$meta.permalink] = newRelation;

    this.calculateVm();
    if (this.config.canOnlyHaveOne) {
      this.showHistory = true;
    }
    if (this.config.isOnlyOne && this.currentRelation) {
      this.pendingLinks[`${this.config.key}${this.newOu.href}`] = { previous: this.currentRelation.$$meta.permalink };
    }
    this.reset();
    if (scopeApply) {
      this.$scope.$apply();
    }
  }

  openRelation(relatedOu) {
    this.reset();
    this.editOu = relatedOu;
    this.editRelation = {
      startDate: relatedOu.resource.startDate,
      endDate: relatedOu.resource.endDate,
    };
    if (this.config.isOnlyOne) {
      this.fetchLinks(this.editOu.href, this.config.thisOu === 'from' ? 'to' : 'from');
    } else if (this.config.canOnlyHaveOne) {
      this.fetchLinks(this.ou.href, this.config.thisOu);
    }
  }

  editRelStartDateChanged(e, value) {
    this.editRelation.startDate = value;
    if (this.previousRelation && (this.config.isRequired || isAfter(this.previousRelation.endDate, value))) {
      this.previousRelation = { ...this.previousRelation, endDate: value };
      this.previousRelVm = this.transformRelatedOu(this.previousRelation);
    }
  }

  editRelEndDateChanged(e, value) {
    this.editRelation.endDate = value;
    if (this.nextRelation && (this.config.isRequired || isBefore(this.nextRelation.startDate, value))) {
      this.nextRelation = { ...this.nextRelation, startDate: value };
      this.nextRelVm = this.transformRelatedOu(this.nextRelation);
    }
  }

  removeRelation() {
    this.relationIsDeleted = true;
    if (this.config.isRequired) {
      if (this.nextRelation) {
        this.nextRelation = { ...this.nextRelation, startDate: this.editOu.resource.startDate };
        this.pendingRelations[this.nextRelation.$$meta.permalink] = this.nextRelation;
        this.nextRelVm = this.transformRelatedOu(this.nextRelation);
        this.$ngRedux.dispatch(addWarningNotification(`De ${this.config.type} relatie tussen ${this.nextRelVm.name} en ${this.editOu.name} die volgt vlak na deze relatie, zal bij opslaan vervroegd worden naar ${this.editOu.resource.startDate}.`));
      } else if (this.previousRelation) {
        this.previousRelation = { ...this.previousRelation, endDate: this.editOu.resource.endDate };
        this.pendingRelations[this.previousRelation.$$meta.permalink] = this.previousRelation;
        this.previousRelVm = this.transformRelatedOu(this.previousRelation);
        this.$ngRedux.dispatch(addWarningNotification(`De vorige ${this.config.type} relatie tussen ${this.previousRelVm.name} en ${this.editOu.name} zal bij opslaan ${this.editOu.resource.endDate ? `verlaat worden naar ${this.editOu.resource.endDate}` : 'terug heropend worden'}.`));
      }
    }
    this.pendingRelations[this.editOu.resource.$$meta.permalink] = {
      ...this.editOu.resource,
      endDate: this.editOu.resource.startDate,
    };
    this.showHistory = true;
    this.calculateVm();
    this.reset();
  }

  applyEditRelation() {
    if (this.editRelation.startDate === this.editOu.resource.startDate && this.editRelation.endDate === this.editOu.resource.endDate) {
      this.$ngRedux.dispatch(addWarningNotification('Er zijn geen wijzigingen aangebracht.'));
      return;
    }
    this.pendingRelations[this.editOu.resource.$$meta.permalink] = {
      ...this.editOu.resource,
      startDate: this.editRelation.startDate,
      endDate: this.editRelation.endDate,
    };
    if (this.previousRelation) {
      this.pendingRelations[this.previousRelation.$$meta.permalink] = this.previousRelation;
    }
    if (this.nextRelation) {
      this.pendingRelations[this.nextRelation.$$meta.permalink] = this.nextRelation;
    }
    if (isBeforeOrEqual(this.editRelation.endDate, getNow())) {
      this.showHistory = true;
    }
    this.calculateVm();
    this.reset();
  }

  toggleRealTime() {
    this.searchRealTime = !this.searchRealTime;
  };
}

export const myRelationsComponents = {
  template: require('./relationsComponents.html'),
  controllerAs: 'ctrl',
  controller: RelationsComponents,
  bindings: {
    config: '<',
    ou: '<',
    vm: '<',
    ouTypeConfig: '<',
    pendingRelations: '<',
    pendingLinks: '<',
    identifiers: '<',
  },
};
